import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { forkJoin } from 'rxjs';
import { DataserviceService } from '../../dataservice.service';
import { AppComponent } from 'src/app/app.component';

@Component({
  selector: 'app-leads-list',
  templateUrl: './leads-list.component.html',
  styleUrl: './leads-list.component.css',
})
export class LeadsListComponent implements OnInit {
  currentUser: any;
  leads: any[] = [];
  filteredLeads: any[] = [];
  isLoading: boolean = false;
  replayLoadingId: string | number | null = null;
  searchTerm: string = '';

  constructor(
    private data: DataserviceService,
    private router: Router,
    private message: NzMessageService,
    private app: AppComponent,
  ) {}

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (this.currentUser.super_admin) {
      this.getAllLeads();
    } else if (this.app.hasPermission('see_all_dish_accounts')) {
      this.getAllLeads();
    } else {
      this.getMyLeads();
    }
  }

  getMyLeads() {
    if (!this.currentUser?.user_id) {
      this.filteredLeads = [];
      return;
    }

    this.isLoading = true;

    forkJoin({
      dish: this.data.getData('customers/dishLeads'),
      starlink: this.data.getData('customers/starlinkLeads'),
    }).subscribe({
      next: (res: any) => {
        const dishLeads = res.dish?.data || [];
        const starlinkLeads = res.starlink?.data || [];
        this.leads = [...dishLeads, ...starlinkLeads];
        this.applyFilter();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load leads', err);
        this.filteredLeads = [];
        this.isLoading = false;
      },
    });
  }

  getAllLeads() {
    this.isLoading = true;

    forkJoin({
      dish: this.data.getData('customers/allDishLeads'),
      starlink: this.data.getData('customers/allStarlinkLeads'),
    }).subscribe({
      next: (res: any) => {
        const dishLeads = res.dish?.data || [];
        const starlinkLeads = res.starlink?.data || [];
        this.leads = [...dishLeads, ...starlinkLeads];
        this.applyFilter();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load leads', err);
        this.filteredLeads = [];
        this.isLoading = false;
      },
    });
  }

  isDishLead(lead: any): boolean {
    return (lead?.provider || '').toLowerCase() === 'dish';
  }

  isStarlinkLead(lead: any): boolean {
    const p = (lead?.provider || '').toLowerCase();
    const o = (lead?.origination || '').toLowerCase();
    return p === 'starlink' || o === 'starlink-flow';
  }

  getStatusColor(status: string | null | undefined): string {
    if (!status) {
      return 'default';
    }

    const lower = status.toLowerCase();

    if (['active', 'complete', 'completed'].includes(lower)) {
      return 'green';
    }

    if (lower.includes('cancel')) {
      return 'red';
    }

    if (lower.includes('pending')) {
      return 'gold';
    }

    return 'blue';
  }

  startReplay(lead: any) {
    if (this.isStarlinkLead(lead)) {
      this.startStarlinkReplay(lead);
      return;
    }

    if (!this.isDishLead(lead)) {
      this.message.info(
        'Replay is only available for Dish and Starlink leads.',
      );
      return;
    }

    if (!lead?.customer_id) {
      this.message.error('Missing customer reference for this lead.');
      return;
    }

    if (this.replayLoadingId) {
      return;
    }

    this.replayLoadingId = lead.customer_id;

    this.data.getData(`customers/leadsById/${lead.customer_id}`).subscribe(
      (res: any) => {
        const detail = res?.data;
        const qual = detail?.qualifygeneric;

        const leadId = detail?.generic?.authenticateCustomerResponse?.LeadId;

        if (!leadId) {
          this.message.warning('Replay data is not ready for this lead yet.');
          this.replayLoadingId = null;
          return;
        }

        this.executeReplay(detail, qual, leadId);
      },
      (err: any) => {
        console.error('Failed to load lead details', err);
        this.message.error('Failed to load lead details.');
        this.replayLoadingId = null;
      },
    );
  }

  private startStarlinkReplay(lead: any) {
    const customerId = lead.customer_id || lead.id;
    if (!customerId) {
      this.message.error('Missing customer reference for this lead.');
      return;
    }

    const customerInfo = {
      firstName: lead.first_name,
      lastName: lead.last_name,
      address: lead.address_line_1,
      city: lead.address_city,
      state: lead.address_state,
      zip: lead.address_zip,
      phone: lead.phone_number,
      email: lead.email,
      permissionToCall: true,
      dob: lead.date_of_birth || '',
    };

    this.router.navigate(['/starlink-flow'], {
      queryParams: {
        customerInfo: JSON.stringify(customerInfo),
        catalog: lead.generic ? JSON.stringify(lead.generic.reserved_sku) : null,
        starlinkOrder: lead.generic ? JSON.stringify(lead.generic.starlink_order_number) : null,
        workOrderId: lead.workorderid ?  JSON.stringify(lead.workorderid) : null,
        customerId,
      },
    });
  }

  private executeReplay(detail: any, qual: any, leadId: any) {
    this.data.postDataNoBodyDotBody({ leadId }, 'dish/getLead').subscribe(
      (r: any) => {
        const response = r?.data?.data;
        const nextOperation = response?.Response?.NextOperation?.Operation?.[0];
        const conversationId = response?.Status?.ConversationID;

        const promoInfo =
          qual?.QualifyCustomerResponse?.PromotionQualificationList
            ?.PromotionQualification?.[0];

        const customerInfo = {
          firstName: detail?.first_name,
          lastName: detail?.last_name,
          address: detail?.address_line_1,
          phone: detail?.phone_number,
          secondPhone: '',
          permissionToCall: true,
          dob: detail?.date_of_birth,
          city: detail?.address_city,
          state: detail?.address_state,
          zip: detail?.address_zip,
        };

        this.router.navigate(['/dish-flow'], {
          queryParams: {
            replay: nextOperation !== 'authenticateCustomer',
            conversationId,
            promoName: promoInfo?.PromotionName ?? false,
            promoId: promoInfo?.QualifiedPromotionId ?? false,
            customerId: detail?.customer_id || null,
            customerInfo: JSON.stringify(customerInfo),
          },
        });
        this.replayLoadingId = null;
      },
      (err: any) => {
        console.error('Failed to replay lead', err);
        this.message.error('Failed to replay lead. Please try again.');
        this.replayLoadingId = null;
      },
    );
  }

  onSearchChange(value: string) {
    this.searchTerm = value;
    this.applyFilter();
  }

  private applyFilter() {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!term) {
      this.filteredLeads = [...this.leads];
      return;
    }

    this.filteredLeads = this.leads.filter((lead: any) => {
      const haystack = [
        lead?.first_name,
        lead?.last_name,
        lead?.provider,
        lead?.phone_number,
        lead?.address_line_1,
        lead?.address_city,
        lead?.status,
      ]
        .map((piece) => (piece || '').toString().toLowerCase())
        .join(' ');

      return haystack.includes(term);
    });
  }
}
